import { TimelineContent, TimelineItem, TimelineSeparator } from '@mui/lab'
import { Box, Button, Typography } from '@mui/material'
import CircleGreenRevised from '../../../assets/icons/Circle Green Revised.png'
import SolidCircle from '../../../assets/icons/Solid Circle.png'
import { OrderStatus } from 'store/orders/orders.types'
import { DateTime } from 'luxon'

interface OrderTrackingTaskProps {
  task: OrderStatus
  /** Used by parent for layout; not used in this component */
  isLast?: boolean
  heading: string
  onMarkComplete?: (task: OrderStatus) => void
}

const OrderTrackingTask = ({
  task,
  isLast = false,
  heading,
  onMarkComplete,
}: OrderTrackingTaskProps) => {
  const isCompleted = task.completed?.status ?? false

  // Format pickup/delivery timestamp (startTime) - display as-is without timezone conversion
  const formatPickupDeliveryTimestamp = (timestamp?: string) => {
    if (!timestamp) return null
    try {
      // Parse as-is without timezone conversion (backend sends timestamps without timezone info)
      const dt = DateTime.fromISO(timestamp)
      if (!dt.isValid) return null
      return {
        date: dt.toFormat('MMM dd, yyyy'),
        time: dt.toFormat('hh:mm a'),
      }
    } catch {
      return null
    }
  }

  // Format status timestamp (assigned/completed/orderCreated) - convert to local time
  const formatStatusTimestamp = (timestamp?: string) => {
    if (!timestamp) return null
    try {
      // Parse as UTC (backend sends timestamps without timezone info, stored as UTC)
      // Then convert to local time for display
      const dt = DateTime.fromISO(timestamp, { zone: 'utc' }).toLocal()
      if (!dt.isValid) return null
      return {
        date: dt.toFormat('MMM dd, yyyy'),
        time: dt.toFormat('hh:mm a'),
      }
    } catch {
      return null
    }
  }

  // Pickup/delivery time - no conversion
  const startTimestamp = formatPickupDeliveryTimestamp(task.startTimestamp)

  const completedTimestamp = formatStatusTimestamp(task.completed?.timestamp)

  // Determine button visibility and text
  const isAssigned = task.assigned?.status ?? false
  const shouldShowActionButton = isAssigned && !isCompleted
  const actionButtonText =
    task.type === 'PICKUP' ? 'Mark as Picked up' : 'Mark as Delivered'

  // Format name: limit to 2 words and convert to uppercase (block letters)
  const formatName = (name: string) => {
    const words = name.trim().split(/\s+/)
    const limitedWords = words.slice(0, 2)
    return limitedWords.join(' ').toUpperCase()
  }

  const displayName = formatName(task.name)

  return (
    <>
      {/* Heading: Pickup / Delivery with tracking color & underline (no bullet) */}
      {heading && (
        <Typography
          sx={{
            fontWeight: 700,
            color: 'secondary.dark',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 0.5,
            // Extra top space when Delivery follows the last Pickup block
            mt: heading === 'Delivery' ? 2 : 0,
            mb: 1.5,
          }}
        >
          {heading}
        </Typography>
      )}

      <TimelineItem>
        <TimelineSeparator>
          {/* Bullet icon before the name */}
          {isCompleted ? (
            <img
              src={CircleGreenRevised}
              alt="check circle green"
              width={16}
              height={16}
            />
          ) : (
            <img src={SolidCircle} alt="solid circle" width={16} height={16} />
          )}
        </TimelineSeparator>
        {/* Consistent bottom padding between stops (scroll instead of compressing) */}
        <TimelineContent
          sx={{
            mt: -0.5,
            mb: 0,
            pb: isLast ? 1 : 3,
            pt: 0,
          }}
        >
          {/* Stop name */}
          <Typography fontWeight={700}>{displayName}</Typography>

          {/* Scheduled Pickup/Delivery */}
          {startTimestamp && (
            <>
              <Typography fontWeight={400} sx={{ mt: 1 }}>
                {task.type === 'PICKUP'
                  ? 'Scheduled Pickup:'
                  : 'Scheduled Delivery:'}
              </Typography>
              <Typography fontWeight={700} sx={{ mt: 0.5 }}>
                {startTimestamp.date} {startTimestamp.time}
              </Typography>
            </>
          )}

          {/* Spacing before assigned/actual sections */}
          <Box sx={{ height: 8 }} />

          {/* Assigned driver */}
          {task.driverName && (
            <>
              <Typography fontWeight={400}>Assigned to:</Typography>
              <Typography fontWeight={700} sx={{ mt: 0.5 }}>
                {task.driverName}
              </Typography>
            </>
          )}

          {/* Action button only when assigned and not completed */}
          {shouldShowActionButton && (
            <Button
              size="small"
              onClick={() => onMarkComplete?.(task)}
              sx={{
                mt: 1.5,
                paddingLeft: '12px',
                paddingRight: '12px',
                paddingTop: '6px',
                paddingBottom: '6px',
                borderRadius: '6px',
                textTransform: 'capitalize',
                fontWeight: 700,
                minWidth: 'auto',
                backgroundColor: 'primary.main',
                color: 'common.white',
                border: 'none',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  boxShadow: 'none',
                },
              }}
            >
              {actionButtonText}
            </Button>
          )}

          {/* Actual pickup/delivery time after completion */}
          {isCompleted && completedTimestamp && (
            <>
              <Typography fontWeight={400} sx={{ mt: 1.5 }}>
                {task.type === 'PICKUP'
                  ? 'Actual Pickup:'
                  : 'Actual Delivery:'}
              </Typography>
              <Typography fontWeight={700} sx={{ mt: 0.5 }}>
                {completedTimestamp.date} {completedTimestamp.time}
              </Typography>
            </>
          )}
        </TimelineContent>
      </TimelineItem>
    </>
  )
}

export default OrderTrackingTask
